import { QueryClient } from '@tanstack/react-query'

import { Group } from '@/models/group'
import { OpsGroup, StoreWithDeployStatus } from '@/models/operations/types'

import { runWithConcurrency } from './actions'
import {
  computeHealth,
  computeSummary,
  createPlaceholderGroup,
  getGroup,
  getGroupsById,
  getGroupsList,
  opsQueryKeys,
  patchStoreInGroupCache,
  seedAllGroupsAsRefreshing,
  setGroup,
  updateGroup,
} from './groupsCache'
import {
  fetchAllGroupsMetadata,
  fetchStoreInfraByGroup,
  fetchStoreInfraById,
  resolveRequestError,
  toOpsGroupBase,
} from './groupInfraFetch'
import { mapStoreInfraToOpsStore } from './storeUtils'
export { getGroup, getGroupsList as getGroups } from './groupsCache'

const GROUP_LIST_CONCURRENCY = 2

let listAllInProgress = false

export class ListInProgressError extends Error {
  constructor() {
    super('A list operation is already in progress')
    this.name = 'ListInProgressError'
  }
}

export function isListAllInProgress() {
  return listAllInProgress
}

export type ListStoreInfoOptions = {
  /** Background refresh — no skeleton (polls, post-bulk sync). */
  silent?: boolean
}

function writeGroupFromStores(
  queryClient: QueryClient,
  groupId: string,
  groupMeta: { id: string; name: string; description: string },
  stores: StoreWithDeployStatus[],
): OpsGroup {
  const summary = computeSummary(stores)
  const { health, healthCount } = computeHealth(summary)
  const opsGroup: OpsGroup = {
    ...toOpsGroupBase(groupMeta),
    summary,
    health,
    healthCount,
    last_refreshed_at: new Date().toISOString(),
    stores,
    isRefreshing: false,
    loadError: null,
  }
  setGroup(queryClient, groupId, opsGroup)
  return opsGroup
}

async function resolveGroupMeta(
  queryClient: QueryClient,
  groupId: string,
): Promise<{ id: string; name: string; description: string }> {
  const fromGroup = getGroup(queryClient, groupId)
  if (fromGroup) {
    return { id: fromGroup.id, name: fromGroup.name, description: fromGroup.description }
  }

  const metadata = await fetchGroupsMetadata(queryClient)
  const found = metadata.find((g) => g.id === groupId)
  if (!found) throw new Error('Group not found')
  return found
}

async function fetchAndCacheGroupStores(
  queryClient: QueryClient,
  groupMeta: { id: string; name: string; description: string },
): Promise<StoreWithDeployStatus[]> {
  try {
    const storeInfraList = await fetchStoreInfraByGroup(groupMeta.id)
    const stores = storeInfraList.map(mapStoreInfraToOpsStore)
    writeGroupFromStores(queryClient, groupMeta.id, groupMeta, stores)
    return stores
  } catch (error) {
    const message = resolveRequestError(error, 'Failed to load stores for this group')
    const previousGroup = getGroup(queryClient, groupMeta.id)

    setGroup(queryClient, groupMeta.id, {
      ...(previousGroup ?? createPlaceholderGroup(groupMeta as Group)),
      isRefreshing: false,
      loadError: message,
      health: 'FAILED',
    })
    throw error
  }
}

/**
 * Single source of truth for store-infra reads and cache updates.
 * - No groupId: list all groups (replaces runRefreshAllGroupsSilent).
 * - groupId, no storeId: list all stores in one group.
 * - groupId + storeId: refresh only that store in cache.
 */
export async function listStoreInfo(
  queryClient: QueryClient,
  groupId?: string,
  storeId?: string,
  options?: ListStoreInfoOptions,
): Promise<StoreWithDeployStatus[]> {
  const silent = options?.silent ?? false

  if (groupId && storeId) {
    const cached = getGroup(queryClient, groupId)
    const existing = cached?.stores.find((s) => s.id === storeId)
    if (!existing) {
      const stores = await listStoreInfo(queryClient, groupId, undefined, options)
      const store = stores.find((s) => s.id === storeId)
      return store ? [store] : []
    }

    const infra = await fetchStoreInfraById(groupId, existing.account_id, storeId)
    const mapped = mapStoreInfraToOpsStore(infra)
    patchStoreInGroupCache(queryClient, groupId, mapped)
    return [mapped]
  }

  if (groupId) {
    const groupMeta = await resolveGroupMeta(queryClient, groupId)

    if (!silent) {
      updateGroup(queryClient, groupId, (current) => {
        if (current) return { ...current, isRefreshing: true, loadError: null }
        return createPlaceholderGroup(groupMeta as Group)
      })
    }

    try {
      return await fetchAndCacheGroupStores(queryClient, groupMeta)
    } catch {
      return getGroup(queryClient, groupId)?.stores ?? []
    }
  }

  if (listAllInProgress) {
    throw new ListInProgressError()
  }

  listAllInProgress = true
  try {
    const metadata = await fetchGroupsMetadata(queryClient)
    seedAllGroupsAsRefreshing(queryClient, metadata)

    await runWithConcurrency(metadata, GROUP_LIST_CONCURRENCY, async (groupMeta) => {
      try {
        await fetchAndCacheGroupStores(queryClient, groupMeta)
      } catch {
        // Per-group error already written to cache.
      }
    })

    return Object.values(getGroupsById(queryClient)).flatMap((group) => group.stores)
  } finally {
    listAllInProgress = false
  }
}

export async function fetchGroupsMetadata(queryClient: QueryClient): Promise<Group[]> {
  const cached = queryClient.getQueryData<Group[]>(opsQueryKeys.groupsMetadata)
  if (cached?.length) return cached
  const metadata = await fetchAllGroupsMetadata()
  queryClient.setQueryData(opsQueryKeys.groupsMetadata, metadata)
  return metadata
}
